import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { AlertTriangle, CalendarClock, CheckCircle2, CircleDollarSign, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react'
import {
  createRoyaltySeriesTerms,
  deleteRoyaltySeriesTerms,
  formatSupabaseLikeError,
  loadRoyaltySeriesTerms,
  updateRoyaltySeriesTerms,
} from '@/services/royaltyService'
import { formatCurrencyWithOptionalKopecks } from '@/lib/utils'
import {
  ContractTermsFormFields,
  createEmptyContractTermsForm,
  DEFAULT_FIXED_ROYALTY_BASE_AMOUNT,
  DEFAULT_LISTOK_CRM_BASE_AMOUNT,
  termModelLabel,
  termSalesModelPayload,
  type ContractTermsFormData,
} from './ContractTermsFormFields'
import type { RoyaltySeriesTerms } from '@/types'
import type { ContractChargeImpact } from '@/services/contractService'

// «Листок»/агентское — только РФ, всегда RUB (бизнес-решение 2026-07-24, план §14.11.8).
// У иностранцев листка нет; движок трактует listok_crm_base_amount как RUB.
const LISTOK_CURRENCY = 'RUB'

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('ru-RU')
}

function addDays(iso: string, n: number): string {
  const d = new Date(iso + 'T00:00:00Z')
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

function termsToForm(terms: RoyaltySeriesTerms): ContractTermsFormData {
  return {
    contract_type: terms.contract_type ?? 'REGULAR',
    sales_model: terms.sales_model === 'MIXED' ? 'MIXED' : 'SUBSCRIPTION',
    effective_from: terms.effective_from?.slice(0, 10) ?? '',
    effective_to: terms.effective_to?.slice(0, 10) ?? '',
    royalty_percent: String(terms.royalty_percent ?? ''),
    agency_percent: terms.agency_percent != null ? String(terms.agency_percent) : '1',
    vat_percent: terms.vat_percent != null ? String(terms.vat_percent) : '22',
    listok_crm_base_amount: terms.listok_crm_base_amount != null
      ? String(terms.listok_crm_base_amount)
      : terms.contract_type === 'SUBSCRIPTION' ? DEFAULT_LISTOK_CRM_BASE_AMOUNT : DEFAULT_FIXED_ROYALTY_BASE_AMOUNT,
    is_listok_crm_carrier: terms.is_listok_crm_carrier === true,
    is_fixed_royalty_carrier: terms.is_fixed_royalty_carrier === true,
    min_royalty_amount: String(terms.min_royalty_amount ?? ''),
    currency_code: terms.currency_code ?? 'RUB',
    // Нормализуем избыточное «== валюте обязательства» к '' (следовать currency_code).
    min_royalty_currency:
      terms.min_royalty_currency && terms.min_royalty_currency !== (terms.currency_code ?? 'RUB')
        ? terms.min_royalty_currency
        : '',
    source_document_note: terms.source_document_note ?? '',
    notes: terms.notes ?? '',
  }
}

type CoverageAction = { kind: 'close' | 'open'; termId: number; endDate?: string }
type CoverageIssue = { level: 'error' | 'warn' | 'ok'; text: string; action?: CoverageAction }
const termsErrorAlertCls =
  'flex items-start gap-2 rounded-md border-2 border-destructive bg-destructive/15 px-3 py-2.5 text-sm font-medium text-destructive shadow-sm'

function TermsErrorAlert({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <div role="alert" aria-live="assertive" className={`${termsErrorAlertCls} ${className}`}>
      <AlertTriangle size={16} className="mt-0.5 flex-none" />
      <span className="min-w-0 break-words">{children}</span>
    </div>
  )
}

export function ContractTermsEditor({
  seriesId,
  contractId,
  contractNumber,
  canEdit,
  chargeImpact,
  coverageStartDate,
  coverageEndDate,
  contractIsEnded = false,
  onChanged,
}: {
  seriesId: number
  contractId: number
  contractNumber: string | null
  canEdit: boolean
  chargeImpact?: ContractChargeImpact | null
  coverageStartDate?: string | null
  coverageEndDate?: string | null
  /**
   * Договор юридически завершён (есть фактическая дата окончания / ДС о расторжении).
   * Пока договор действует (в т.ч. «истёк, признаётся действующим») — условия должны
   * оставаться бессрочными и «продлеваться» вместе с ним; при завершении — закрываться на
   * дату окончания договора. См. P-05, docs/royalty-map/70-improvement-backlog.md.
   */
  contractIsEnded?: boolean
  onChanged?: () => void
}) {
  const [terms, setTerms] = useState<RoyaltySeriesTerms[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [editingTermId, setEditingTermId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [togglingCarrierId, setTogglingCarrierId] = useState<number | null>(null)
  const [syncingTermId, setSyncingTermId] = useState<number | null>(null)
  const [form, setForm] = useState(createEmptyContractTermsForm)

  const load = useCallback(() => {
    setLoading(true)
    loadRoyaltySeriesTerms(seriesId)
      .then(data => {
        setTerms(data)
        setError(null)
      })
      .catch(e => setError(formatSupabaseLikeError(e)))
      .finally(() => setLoading(false))
  }, [seriesId])

  useEffect(() => {
    load()
  }, [load])

  const related = useMemo(
    () => terms.filter(t => t.contract_id === contractId),
    [terms, contractId]
  )
  const otherTermsCount = terms.length - related.length

  // Согласованность условий с договором (P-05). Условия хранятся по серии и лишь ссылаются
  // на договор. При расторжении договора база автоматически закрывает открытые/выходящие за дату окончания условия;
  // здесь показываем оставшиеся дыры/нахлёсты и
  // предлагаем привести даты к сроку договора. Правило: договор действует → последний интервал
  // условий должен быть бессрочным; договор завершён → интервал закрывается на дату окончания.
  const coverageIssues = useMemo<CoverageIssue[]>(() => {
    if (!coverageStartDate) return []
    const start = coverageStartDate.slice(0, 10)
    const legalEnd = contractIsEnded ? (coverageEndDate?.slice(0, 10) || null) : null
    const intervals = [...related].sort((a, b) => a.effective_from.localeCompare(b.effective_from))
    const issues: CoverageIssue[] = []

    if (intervals.length === 0) {
      return [{
        level: 'error',
        text: contractIsEnded && legalEnd
          ? `Нет условий на срок договора с ${formatDate(start)} по ${formatDate(legalEnd)}.`
          : `Нет условий с даты начала договора ${formatDate(start)}.`,
      }]
    }

    // 1. Нахлёсты между редакциями одного договора.
    intervals.forEach((term, index) => {
      const next = intervals[index + 1]
      if (!next) return
      const termEnd = term.effective_to?.slice(0, 10) || null
      if (!termEnd || termEnd >= next.effective_from.slice(0, 10)) {
        issues.push({
          level: 'error',
          text: `Редакции #${term.term_id} и #${next.term_id} пересекаются с ${formatDate(next.effective_from)}.`,
        })
      }
    })

    // 2. Дыры внутри юридического срока договора.
    let cursor = start
    let openEnded = false
    for (const term of intervals) {
      const tStart = term.effective_from.slice(0, 10)
      const tEnd = term.effective_to?.slice(0, 10) || null
      if (tEnd && tEnd < cursor) continue
      if (legalEnd && tStart > legalEnd) break
      if (tStart > cursor) {
        const gapEnd = legalEnd && legalEnd < addDays(tStart, -1)
          ? legalEnd
          : addDays(tStart, -1)
        if (cursor <= gapEnd) {
          issues.push({ level: 'error', text: `Нет условий с ${formatDate(cursor)} по ${formatDate(gapEnd)}.` })
        }
      }
      if (!tEnd) { openEnded = true; break }
      const nextCursor = addDays(tEnd, 1)
      if (nextCursor > cursor) cursor = nextCursor
    }
    if (contractIsEnded && legalEnd && cursor <= legalEnd && !openEnded) {
      issues.push({ level: 'error', text: `Нет условий с ${formatDate(cursor)} по ${formatDate(legalEnd)}.` })
    }

    // 3. Выравнивание конца условий по юридическому состоянию договора.
    const last = intervals[intervals.length - 1]
    if (contractIsEnded && legalEnd) {
      if (openEnded) {
        issues.push({
          level: 'warn',
          text: `Договор завершён ${formatDate(legalEnd)}, а условия открыты (бессрочно).`,
          action: { kind: 'close', termId: last.term_id, endDate: legalEnd },
        })
      } else {
        const lastEnd = last.effective_to?.slice(0, 10) || null
        if (lastEnd && lastEnd > legalEnd) {
          issues.push({
            level: 'warn',
            text: `Условия действуют до ${formatDate(lastEnd)} — дольше срока договора (${formatDate(legalEnd)}).`,
            action: { kind: 'close', termId: last.term_id, endDate: legalEnd },
          })
        }
      }
    } else if (!openEnded) {
      const lastEnd = last.effective_to?.slice(0, 10) || null
      issues.push({
        level: 'warn',
        text: `Договор действует, но условия закрыты${lastEnd ? ` ${formatDate(lastEnd)}` : ''}. Пока договор не завершён, условия должны действовать бессрочно.`,
        action: { kind: 'open', termId: last.term_id },
      })
    }

    if (issues.length === 0) {
      issues.push({
        level: 'ok',
        text: contractIsEnded && legalEnd
          ? `Условия покрывают договор до даты окончания ${formatDate(legalEnd)}.`
          : 'Условия покрывают договор и действуют, пока действует договор.',
      })
    }
    return issues
  }, [coverageEndDate, coverageStartDate, contractIsEnded, related])

  function setField<K extends keyof ContractTermsFormData>(
    field: K,
    value: ContractTermsFormData[K]
  ) {
    setForm(f => {
      if (field === 'contract_type' && value !== f.contract_type) {
        const previousDefault = f.contract_type === 'SUBSCRIPTION' ? DEFAULT_LISTOK_CRM_BASE_AMOUNT : DEFAULT_FIXED_ROYALTY_BASE_AMOUNT
        const nextDefault = value === 'SUBSCRIPTION' ? DEFAULT_LISTOK_CRM_BASE_AMOUNT : DEFAULT_FIXED_ROYALTY_BASE_AMOUNT
        return { ...f, contract_type: value as ContractTermsFormData['contract_type'], listok_crm_base_amount: f.listok_crm_base_amount === previousDefault ? nextDefault : f.listok_crm_base_amount }
      }
      return { ...f, [field]: value }
    })
  }

  async function handleSave() {
    if (!form.effective_from || !form.royalty_percent) return
    setSaving(true)
    setError(null)
    try {
      const isSubscription = form.contract_type === 'SUBSCRIPTION'
      const payload = {
        contract_id: contractId,
        source_contract_id: contractId,
        contract_type: form.contract_type,
        sales_model: termSalesModelPayload(form),
        effective_from: form.effective_from,
        effective_to: form.effective_to || null,
        royalty_percent: parseFloat(form.royalty_percent),
        agency_percent: isSubscription ? parseFloat(form.agency_percent || '0') : null,
        vat_percent: isSubscription ? parseFloat(form.vat_percent || '0') : null,
        // Обычному терму база нужна только под фиксированную часть роялти.
        listok_crm_base_amount: isSubscription
          ? parseFloat(form.listok_crm_base_amount || '0')
          : form.is_fixed_royalty_carrier ? parseFloat(form.listok_crm_base_amount || DEFAULT_FIXED_ROYALTY_BASE_AMOUNT) : null,
        is_listok_crm_carrier: isSubscription ? form.is_listok_crm_carrier : false,
        is_fixed_royalty_carrier: !isSubscription ? form.is_fixed_royalty_carrier : false,
        min_royalty_amount: parseFloat(form.min_royalty_amount || '0'),
        currency_code: form.currency_code,
        // NULL = минималка в валюте обязательства; отдельную валюту минимума больше не заводим.
        min_royalty_currency: null,
        source_document_note: form.source_document_note.trim() || null,
        notes: form.notes.trim() || null,
      }
      if (editingTermId != null) {
        await updateRoyaltySeriesTerms(editingTermId, payload)
      } else {
        await createRoyaltySeriesTerms({
          series_id: seriesId,
          ...payload,
        })
      }
      setShowForm(false)
      setEditingTermId(null)
      setForm(createEmptyContractTermsForm())
      load()
      onChanged?.()
    } catch (e) {
      setError(formatSupabaseLikeError(e))
    } finally {
      setSaving(false)
    }
  }

  function startCreate() {
    setEditingTermId(null)
    setForm(createEmptyContractTermsForm())
    setShowForm(true)
  }

  function startEdit(term: RoyaltySeriesTerms) {
    setEditingTermId(term.term_id)
    setForm(termsToForm(term))
    setShowForm(true)
  }

  function cancelForm() {
    setShowForm(false)
    setEditingTermId(null)
    setForm(createEmptyContractTermsForm())
  }

  async function handleDelete(termId: number) {
    if (!confirm('Удалить интервал условий? Если он покрывает действующий период договора, появится gap, а новое начисление роялти может быть заблокировано.')) return
    setDeletingId(termId)
    setError(null)
    try {
      await deleteRoyaltySeriesTerms(termId)
      load()
      onChanged?.()
    } catch (e) {
      setError(formatSupabaseLikeError(e))
    } finally {
      setDeletingId(null)
    }
  }

  // P-05: привести даты условий к сроку договора одним действием. «close» — закрыть интервал
  // на дату окончания договора (договор завершён); «open» — сделать бессрочным (договор
  // действует). Пишем через тот же update, отдельного RPC не заводим.
  async function applyCoverageAction(action: CoverageAction) {
    setSyncingTermId(action.termId)
    setError(null)
    try {
      await updateRoyaltySeriesTerms(action.termId, {
        effective_to: action.kind === 'close' ? (action.endDate ?? null) : null,
      })
      load()
      onChanged?.()
    } catch (e) {
      setError(formatSupabaseLikeError(e))
    } finally {
      setSyncingTermId(null)
    }
  }

  // P-22: пометить/снять «договор-носитель CRM-фикса LISTOK_CRM». Фикс начисляется один
  // раз с выбранного договора-носителя, а не с каждой подписочной серии франчайзи.
  async function handleToggleListokCarrier(term: RoyaltySeriesTerms) {
    setTogglingCarrierId(term.term_id)
    setError(null)
    try {
      await updateRoyaltySeriesTerms(term.term_id, { is_listok_crm_carrier: !term.is_listok_crm_carrier })
      load()
      onChanged?.()
    } catch (e) {
      setError(formatSupabaseLikeError(e))
    } finally {
      setTogglingCarrierId(null)
    }
  }

  async function handleToggleFixedRoyaltyCarrier(term: RoyaltySeriesTerms) {
    setTogglingCarrierId(term.term_id)
    setError(null)
    try {
      await updateRoyaltySeriesTerms(term.term_id, {
        is_fixed_royalty_carrier: !term.is_fixed_royalty_carrier,
        ...(!term.is_fixed_royalty_carrier && term.listok_crm_base_amount == null
          ? { listok_crm_base_amount: Number(DEFAULT_FIXED_ROYALTY_BASE_AMOUNT) }
          : {}),
      })
      load()
      onChanged?.()
    } catch (e) {
      setError(formatSupabaseLikeError(e))
    } finally {
      setTogglingCarrierId(null)
    }
  }

  return (
    <div className="mt-4 border-t border-border pt-3">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="text-sm font-semibold">Условия роялти</div>
          <p className="text-[11px] text-muted-foreground">
            MAIN-договор владелец: #{contractId}
            {contractNumber ? ` (${contractNumber})` : ''}.
            {otherTermsCount > 0 ? ` Ещё ${otherTermsCount} интервалов принадлежат другим договорам серии.` : ''}
          </p>
          <p className="mt-1 text-[10px] leading-4 text-muted-foreground">
            Условия роялти хранятся отдельно от договора и лишь ссылаются на него: договор задаёт
            юридический срок, условия — сколько и как начисляем. При расторжении договора открытые условия закрываются
            автоматически по дате окончания; держите интервалы условий без дыр и нахлёстов по сроку договора.
          </p>
        </div>
      </div>

      {error && (
        <TermsErrorAlert className="mb-3 text-xs">{error}</TermsErrorAlert>
      )}

      {canEdit && chargeImpact && chargeImpact.total > 0 && (
        <div className="mb-2 rounded-md border border-warning/40 bg-warning/10 px-2.5 py-2 text-xs text-warning">
          У этого договора уже есть начисления. Изменение условий не пересчитает существующие charge автоматически.
        </div>
      )}
      {canEdit && chargeImpact && chargeImpact.protectedPeriods.length > 0 && (
        <div className="mb-2 rounded-md border border-destructive/40 bg-destructive/10 px-2.5 py-2 text-xs text-destructive">
          Зафиксированные периоды: {chargeImpact.protectedPeriods.map(period => `${String(period.month).padStart(2, '0')}.${period.year}`).join(', ')}.
          Условия можно менять только вне этих дат; пересекающую их операцию база отклонит.
          Для исправления сначала отмените выставление роялти и снимите фиксацию выручки.
        </div>
      )}

      {coverageIssues.length > 0 && (
        <div className="mb-2 space-y-1.5">
          {coverageIssues.map((issue, index) => {
            const tone =
              issue.level === 'error'
                ? 'border-destructive/40 bg-destructive/10 text-destructive'
                : issue.level === 'warn'
                  ? 'border-warning/40 bg-warning/10 text-warning'
                  : 'border-emerald-500/40 bg-emerald-500/10 text-emerald-600'
            const Icon = issue.level === 'ok' ? CheckCircle2 : issue.level === 'warn' ? CalendarClock : AlertTriangle
            return (
              <div
                key={index}
                className={`flex items-start justify-between gap-2 rounded-md border px-2.5 py-2 text-xs ${tone}`}
              >
                <span className="flex items-start gap-1.5">
                  <Icon size={13} className="mt-0.5 shrink-0" />
                  <span>{issue.text}</span>
                </span>
                {canEdit && issue.action && (
                  <button
                    type="button"
                    onClick={() => issue.action && applyCoverageAction(issue.action)}
                    disabled={syncingTermId === issue.action.termId}
                    className="shrink-0 whitespace-nowrap rounded border border-current px-2 py-1 text-[11px] font-medium disabled:opacity-50"
                  >
                    {syncingTermId === issue.action.termId
                      ? 'Сохранение…'
                      : issue.action.kind === 'close'
                        ? 'Закрыть по договору'
                        : 'Сделать бессрочными'}
                  </button>
                )}
              </div>
            )
          })}
        </div>
      )}

      {loading ? (
        <div className="text-xs text-muted-foreground py-3">Загрузка условий...</div>
      ) : (
        <div className="space-y-2">
          {related.length === 0 && !showForm && (
            <div className="text-center text-muted-foreground py-4 text-sm">
              Для этого договора интервалы условий не заведены
            </div>
          )}

          {related.map(t => (
            <div key={t.term_id} className="rounded-md border border-border p-3">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div>
                  <div className="text-lg font-bold text-primary">{t.royalty_percent}%</div>
                  <div className="text-xs text-muted-foreground">от выручки</div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => startEdit(t)}
                      disabled={saving || deletingId === t.term_id}
                      className="p-1 text-muted-foreground hover:text-primary disabled:opacity-50"
                      title="Редактировать интервал"
                    >
                      <Pencil size={13} />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(t.term_id)}
                      disabled={deletingId === t.term_id}
                      className="p-1 text-muted-foreground hover:text-destructive disabled:opacity-50"
                      title="Удалить интервал"
                    >
                      {deletingId === t.term_id
                        ? <RefreshCw size={13} className="animate-spin" />
                        : <Trash2 size={13} />}
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-muted-foreground">Модель: </span>
                  <span className="font-medium">{termModelLabel(t)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">С: </span>
                  <span className="font-medium">{formatDate(t.effective_from)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">По: </span>
                  <span className="font-medium">{t.effective_to ? formatDate(t.effective_to) : 'бессрочно'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Минимум: </span>
                  <span className="font-medium">{formatCurrencyWithOptionalKopecks(t.min_royalty_amount, t.min_royalty_currency ?? t.currency_code)}</span>
                  {t.min_royalty_currency && t.min_royalty_currency !== t.currency_code && (
                    <span className="ml-1 text-[10px] text-muted-foreground">
                      (≈ в {t.currency_code} на дату начисления)
                    </span>
                  )}
                </div>
                {t.contract_type === 'SUBSCRIPTION' && (
                  <>
                    <div>
                      <span className="text-muted-foreground">Агентское: </span>
                      <span className="font-medium">{t.agency_percent ?? 0}%</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">НДС: </span>
                      <span className="font-medium">{t.vat_percent ?? 22}%</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Ручное агентское: </span>
                      <span className="font-medium">{formatCurrencyWithOptionalKopecks(t.listok_crm_base_amount ?? 0, LISTOK_CURRENCY)}</span>
                    </div>
                  </>
                )}
              </div>
              {t.source_document_note && (
                <div className="mt-1 text-[10px] text-muted-foreground">{t.source_document_note}</div>
              )}
              {t.notes && (
                <div className="mt-1 text-[10px] text-muted-foreground italic">{t.notes}</div>
              )}
              {t.contract_type === 'SUBSCRIPTION' && (
                canEdit ? (
                  <label className="mt-2 flex items-start gap-2 rounded-md border border-border bg-muted/40 px-2 py-1.5 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={t.is_listok_crm_carrier}
                      disabled={togglingCarrierId === t.term_id || saving}
                      onChange={() => handleToggleListokCarrier(t)}
                    />
                    <span className="flex items-center gap-1.5">
                      <CircleDollarSign size={13} className="text-primary shrink-0" />
                      <span>
                        Начислять агентский ручной платёж с этого договора
                        <span className="block text-[10px] text-muted-foreground">
                          Ежемесячное ручное агентское начисляется один раз — только с этого
                          договора, а не с каждой подписочной студии франчайзи. Если у франчайзи несколько студий
                          в одной CRM, отметьте только один договор.
                          {togglingCarrierId === t.term_id ? ' Сохранение…' : ''}
                        </span>
                      </span>
                    </span>
                  </label>
                ) : t.is_listok_crm_carrier ? (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-primary">
                    <CircleDollarSign size={12} /> Агентский ручной платёж начисляется с этого договора
                  </div>
                ) : null
              )}
              {t.contract_type === 'REGULAR' && (
                canEdit ? (
                  <label className="mt-2 flex items-start gap-2 rounded-md border border-border bg-muted/40 px-2 py-1.5 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={t.is_fixed_royalty_carrier}
                      disabled={togglingCarrierId === t.term_id || saving || (t.currency_code !== 'RUB' && !t.is_fixed_royalty_carrier)}
                      onChange={() => handleToggleFixedRoyaltyCarrier(t)}
                    />
                    <span className="flex items-center gap-1.5">
                      <CircleDollarSign size={13} className="text-primary shrink-0" />
                      <span>
                        Начислять фиксированную часть роялти с этого договора
                        <span className="block text-[10px] text-muted-foreground">
                          {t.currency_code === 'RUB'
                            ? `${Number(t.listok_crm_base_amount ?? DEFAULT_FIXED_ROYALTY_BASE_AMOUNT).toLocaleString('ru-RU')} ₽ в месяц без НДС; сумму месяца можно изменить корректировкой при выставлении.`
                            : 'Только для договоров с роялти в рублях.'}
                          {togglingCarrierId === t.term_id ? ' Сохранение…' : ''}
                        </span>
                      </span>
                    </span>
                  </label>
                ) : t.is_fixed_royalty_carrier ? (
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-primary">
                    <CircleDollarSign size={12} /> Фиксированная часть роялти начисляется с этого договора
                  </div>
                ) : null
              )}
            </div>
          ))}

          {canEdit && (
            showForm ? (
              <div className={`rounded-md border border-l-4 p-3 ${error ? 'border-destructive border-l-destructive bg-destructive/5' : 'border-border border-l-blue-400'}`}>
                <p className="text-sm font-medium mb-3">
                  {editingTermId == null ? 'Новый интервал' : 'Редактирование интервала'}
                </p>
                <div>
                  <ContractTermsFormFields form={form} onChange={setField} modelLocked={editingTermId != null} />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={cancelForm}
                      className="text-xs text-muted-foreground"
                    >
                      Отмена
                    </button>
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving || !form.effective_from || !form.royalty_percent}
                      className="text-xs bg-primary text-primary-foreground px-3 py-1.5 rounded-lg disabled:opacity-50"
                    >
                      {saving ? 'Сохранение...' : editingTermId == null ? 'Добавить' : 'Сохранить'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={startCreate}
                className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground border border-dashed border-border rounded-lg py-2.5 hover:text-foreground hover:border-foreground transition-colors"
              >
                <Plus size={13} /> Добавить интервал
              </button>
            )
          )}

          {!canEdit && (
            <div className="text-[10px] text-muted-foreground">
              Редактирование условий доступно только пользователям УК с расширенным доступом.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
